/**
 * « Corriger la qualification » from the call history (W4, ADR-047 §4,
 * DES-17). The server owns the rules (QualificationService): a correction is
 * revision n+1 with expected_revision = the revision that stands, accepted
 * for 24 h after the first commit. These helpers only decide what the
 * history shows.
 */
export const CORRECTION_WINDOW_MS = 24 * 3600 * 1000;

/**
 * The row offers « Corriger » when the call was qualified through the new
 * workflow (only its commit sets human_qualified_at) less than 24 h ago.
 * human_qualified_at moves with each correction while the window starts at
 * the first commit: the dialog reads the exact window from the server and
 * says when it has closed.
 */
export function canOfferCorrection(call: { human_qualified_at?: string | null }, now: number = Date.now()): boolean {
  if (!call.human_qualified_at) return false;
  const at = Date.parse(call.human_qualified_at);
  return Number.isFinite(at) && now - at < CORRECTION_WINDOW_MS;
}

/** Codes the dialog explains in its own words; anything else is a generic failure. */
const KNOWN = new Set([
  'QUALIFICATION_STALE', 'QUALIFICATION_CORRECTION_EXPIRED', 'QUALIFICATION_CALL_NOT_FOUND',
  'QUALIFICATION_CALLBACK_DATE_REQUIRED', 'QUALIFICATION_NOT_PENDING', 'IDEMPOTENCY_CONFLICT', 'NETWORK',
]);

/** dialer:callHistory.correct.errors.<key> */
export function correctionErrorKey(code: string | undefined): string {
  return code && KNOWN.has(code) ? code : 'FAILED';
}

/** What the user can do next after an error. */
export function correctionErrorAction(code: string | undefined): 'reload' | 'retry' | 'none' {
  if (code === 'QUALIFICATION_STALE') return 'reload';
  if (code === 'QUALIFICATION_CORRECTION_EXPIRED' || code === 'QUALIFICATION_CALL_NOT_FOUND') return 'none';
  return 'retry';
}

/** DES-17: what the correction could not undo, to fix by hand. */
export function manualFixes(effects: readonly string[] | undefined): Array<'meeting_manual_fix' | 'crm_manual_fix'> {
  const out: Array<'meeting_manual_fix' | 'crm_manual_fix'> = [];
  if (effects?.includes('meeting_manual_fix')) out.push('meeting_manual_fix');
  if (effects?.includes('crm_manual_fix')) out.push('crm_manual_fix');
  return out;
}

/** The window is closed (correctable_until in the past). */
export function windowClosed(correctableUntil: string | null, now: number = Date.now()): boolean {
  if (!correctableUntil) return false;
  const until = Date.parse(correctableUntil);
  return Number.isFinite(until) && now >= until;
}
